import { Alert, AlertIcon, Button, ButtonGroup, Stack, Text, useToast } from '@chakra-ui/react';
import { getTenantByHost } from '@pelicargo/tenants';
import { modelUtils } from '@pelicargo/utils';
import { useMemo } from 'react';

import { useProviderBookingConfirmation } from '../../hooks/quote/useProviderBookingConfirmation';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useRequestDrawersContext } from '../RequestDrawers';

export const SpotRateProviderBanner = () => {
  const { request, quotes } = useCurrentRequest();
  const { openModal } = useRequestDrawersContext();
  const { setConfirmation, isLoading } = useProviderBookingConfirmation();
  const toast = useToast();

  const needsConfirmation = useMemo(
    () => modelUtils.request.needsProviderSpotConfirmation(request, quotes),
    [request, quotes],
  );
  const bookedQuoteId = useMemo(() => modelUtils.request.bookedQuote(request, quotes)?.id, [request, quotes]);

  const showBanner = needsConfirmation && getTenantByHost().isSystemTenant;

  if (!showBanner) return null;

  const handleDismiss = async () => {
    await setConfirmation(bookedQuoteId, true);

    toast({
      duration: 8000,
      isClosable: true,
      render: ({ onClose }) => (
        <Alert status="success" borderRadius="md" alignItems="center" maxW="sm" boxShadow="lg">
          <AlertIcon />
          <Text flex={1} mr={3}>
            Marked as confirmed
          </Text>
          <Button
            size="sm"
            variant="link"
            flexShrink={0}
            onClick={async () => {
              // Clearing the stamp brings the banner back.
              await setConfirmation(bookedQuoteId, false);
              onClose();
            }}
          >
            Undo
          </Button>
        </Alert>
      ),
    });
  };

  return (
    <Alert status="warning" alignItems="flex-start" borderBottomWidth={1}>
      <AlertIcon />
      <Stack spacing={2}>
        <Text fontWeight="semibold">Spot rate not yet confirmed with the carrier</Text>
        <Text fontSize="sm">
          This booking has an AWB but the carrier hasn&apos;t been told the spot rate was accepted. Send the
          confirmation so they apply the spot rate instead of the standard rate.
        </Text>
        <ButtonGroup size="sm" spacing={1} alignSelf="flex-start" isDisabled={isLoading}>
          <Button colorScheme="orange" onClick={() => openModal('AirlineBooking')}>
            Confirm spot rate with carrier
          </Button>
          <Button variant="ghost" colorScheme="orange" onClick={handleDismiss}>
            Mark as handled
          </Button>
        </ButtonGroup>
      </Stack>
    </Alert>
  );
};
